import { Badge } from '@/components/ui/badge'
import { IconButton } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { CollectorAction, CollectorBillingPeriodData } from '@/domain/collectors/collector'
import { BillingType, InvoiceData, VoucherType } from '@/domain/invoices/invoice'
import { BillingFrequency } from '@/domain/offerings/offering'
import { PaymentSource, PaymentStatus } from '@/domain/payments/payments'
import { ColumnDef } from '@tanstack/react-table'
import { AlertTriangle, ArrowRight } from 'lucide-react'
import { DownloadButton } from '../agreements/listing/sub-components/DownloadButton'
import { serviceTypeBadge } from '../offerings/common'
import { formatCurrency, renderDatetime, renderSinglePeriod } from '../shared/utils'

const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']

export const formatBillingPeriodDescription = (source: PaymentSource, description: string, periodStart: Date, billingFrequency: BillingFrequency): React.ReactNode => {
  if (source === PaymentSource.Direct) {
    return <div className="whitespace-pre-line"> {description}</div>
  }
  else if (source === PaymentSource.Installment) {
    return <div className="flex flex-col gap-0.5 items-start">
      <span className='w-[max-content]'>{description}</span>
      <span className='text-muted-foreground tracking-tight'>{renderSinglePeriod(periodStart, '/')}</span>
    </div>

  }
  else if (source === PaymentSource.Subscription) {
    if (description)
      return <div className="flex flex-col gap-0.5 items-start">
        <span className='w-[max-content]'>{description}</span>
        <span className='text-muted-foreground tracking-tight'>
          {(billingFrequency == BillingFrequency.ANNUAL) ? `Año ${periodStart.getFullYear()}` : `${monthNames[periodStart.getMonth()]}/${periodStart.getFullYear()}`}
        </span>
      </div>
    return (billingFrequency == BillingFrequency.ANNUAL) ? `Año ${periodStart.getFullYear()}` : `${monthNames[periodStart.getMonth()]}/${periodStart.getFullYear()}`
  }
  else return <div className="whitespace-pre-line"> {description}</div>
}

export const formatPaymentConcept = (source: PaymentSource, description: string, periodStart: Date, billingFrequency: BillingFrequency): string => {
  if (source === PaymentSource.Direct) {
    return description
  }
  else if (source === PaymentSource.Installment) {
    return `${description} - ${renderSinglePeriod(periodStart, '/')}`

  }
  else if (source === PaymentSource.Subscription) {
    if (description)
      return description
    return (billingFrequency == BillingFrequency.ANNUAL) ? `Año ${periodStart.getFullYear()}` : `${monthNames[periodStart.getMonth()]}/${periodStart.getFullYear()}`
  }
  else return description
}

export const formatDate = (date: Date | null): string => {
  if (!date) return '-'
  return new Date(date).toLocaleDateString('es-AR')
}

export const formatPaymentStatus = ({ status }: { status: PaymentStatus }) => {
  switch (status) {
    case PaymentStatus.PENDING:
      return <Badge variant="warning">Pendiente</Badge>
    case PaymentStatus.COMPLETED:
      return <Badge variant="success">Pagado</Badge>
    case PaymentStatus.FAILED:
      return <Badge variant="destructive">Error</Badge>
    case PaymentStatus.REFUNDED:
      return <Badge variant="outline">Reembolsado</Badge>
    case PaymentStatus.CANCELLED:
      return <Badge variant="secondary">Cancelado</Badge>
    case PaymentStatus.IN_COLLECTOR:
      return <Badge variant="info">Cobrador</Badge>
    default:
      return <Badge variant="destructive">Error</Badge>
  }
}

export const statusLabels: Record<string, string> = {
  pending: 'Pendiente',
  paid: 'Pagado',
  overdue: 'Vencido',
  cancelled: 'Cancelado',
  partial: 'Parcial',
}

export const statusVariant: Record<string, string> = {
  pending: 'secondary',
  paid: 'default',
  overdue: 'destructive',
  cancelled: 'outline',
  partial: 'success',
}

export const createBillingPeriodColumns = (
  onActionClick?: (contractId: string) => void,
  showSelection: boolean = true,
  selectedAction?: CollectorAction | 'none',
  billingType?: BillingType,
  invoicesByPaymentId?: Map<string, InvoiceData[]>,
  onOpenDocument?: (args: { documentId: string; fileName: string }) => void,
  contractsNeedingReview?: Map<string, { name: string; dateOfBirth: Date }>
): ColumnDef<CollectorBillingPeriodData>[] => {
  const columns: ColumnDef<CollectorBillingPeriodData>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => {
            table.toggleAllPageRowsSelected(!!value)
          }}
          aria-label="Seleccionar todos"
          onClick={(e) => e.stopPropagation()}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Seleccionar fila"
          onClick={(e) => e.stopPropagation()}
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'n',
      header: 'N°',
      cell: ({ row }) => row.index + 1,
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'contractNumber',
      header: 'Contrato',
    },
    {
      accessorKey: 'offeringName',
      header: 'Artículo',
      cell: ({ row }) => {
        const offering = row.original
        return `${offering.offeringCode || ''}`
      },
    },
    {
      accessorKey: 'serviceType',
      header: 'Tipo de servicio',
      cell: ({ row }) => {
        const serviceType = row.original.serviceType
        return serviceTypeBadge(serviceType)
      },
    },
    {
      accessorKey: 'cliente',
      header: 'Cliente',
      cell: ({ row }) => {
        const client = row.original
        return <div className="flex flex-col">
          <span className="font-medium">{client.clientName || ''} ({client.clientCode})</span>
          <span className="text-xs text-muted-foreground">{client.clientAddress || ''}</span>
        </div>
      },
    },
    {
      accessorKey: 'description',
      header: 'Concepto',
      cell: ({ row }) => {
        const { source, description, periodStart, billingFrequency } = row.original
        return formatBillingPeriodDescription(source, description, periodStart, billingFrequency)
      },
    },
    {
      accessorKey: 'paymentStatus',
      header: 'Estado',
      cell: ({ row }) => {
        const status = row.original.paymentStatus
        return formatPaymentStatus({ status })
      },
    },
    {
      accessorKey: 'totalAmount',
      header: 'Total',
      cell: ({ row }) => formatCurrency(row.original.totalAmount),
    },
    {
      accessorKey: 'outstandingAmount',
      header: 'Saldo',
      cell: ({ row }) => formatCurrency(row.original.outstandingAmount),
    },
    {
      accessorKey: 'updateAt',
      header: 'Últ. actualización',
      cell: ({ row }) => renderDatetime(row.original.updatedAt),
    },
    {
      id: 'actions',
      header: 'Acciones',
      cell: ({ row }) => (
        <div className="flex justify-end">
          <IconButton variant="default" title="Ver contrato" onClick={() => onActionClick?.(row.original.contractId)}>
            <ArrowRight className="h-4 w-4" />
          </IconButton>
        </div>
      ),
    },
  ]

  if (!showSelection) {
    const filtered = columns.filter((col) => col.id !== 'select' && (col as any).accessorKey !== 'updateAt')
    if (selectedAction && selectedAction !== 'none') {
      const actionsIndex = filtered.findIndex((col) => col.id === 'actions')
      if (actionsIndex !== -1) {
        filtered[actionsIndex] = {
          id: 'impacto',
          header: 'Impacto',
          cell: ({ row }) => {
            const { totalAmount, paymentStatus, paymentId, contractId } = row.original
            const amount = formatCurrency(totalAmount)
            const canShowDocument = paymentStatus === PaymentStatus.IN_COLLECTOR || paymentStatus === PaymentStatus.COMPLETED
            const invoices = canShowDocument ? invoicesByPaymentId?.get(paymentId) ?? [] : []
            const reviewData = contractsNeedingReview?.get(contractId)

            let impactLabel: React.ReactNode = null
            if (selectedAction === CollectorAction.TO_COLLECTOR && !canShowDocument) {
              impactLabel = billingType === BillingType.Invoice
                ? <span className="text-destructive font-semibold">Factura por {amount}</span>
                : <span>Recibo por {amount}</span>
            } else if (selectedAction === CollectorAction.FROM_COLLECTOR && canShowDocument) {
              impactLabel = invoices[0]?.voucherType === VoucherType.INVOICE
                ? <span className="text-destructive font-semibold">Nota de crédito por {amount}</span>
                : <span>Devolución por {amount}</span>
            } else if (selectedAction === CollectorAction.PAYED_COLLECTOR && paymentStatus === PaymentStatus.COMPLETED) {
              impactLabel = <span>Entregado {amount}</span>
            } else if (selectedAction === CollectorAction.PAYED_COLLECTOR && paymentStatus === PaymentStatus.IN_COLLECTOR) {
              impactLabel = <span>Rendición por {amount}</span>
            } else if (selectedAction === CollectorAction.PRINT_INVOICES) {
              impactLabel = <span>Comprobante por {amount}</span>
            }

            return (
              <div className="flex flex-col gap-1">
                {reviewData && (
                  <div className="flex items-center gap-1 text-red-600 font-semibold text-xs">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    <span>{reviewData.name} - cumple 18 años</span>
                  </div>
                )}
                {impactLabel}
                {(canShowDocument || selectedAction === CollectorAction.PRINT_INVOICES) && onOpenDocument && (
                  <DownloadButton invoices={invoices} onOpenDocument={onOpenDocument} />
                )}
              </div>
            )
          },
        }
      }
    }
    return filtered
  }

  return columns
}

export const billingPeriodsSummary = (data: CollectorBillingPeriodData[], selectedIds: Set<string>) => {
  const arr = Array.isArray(data) ? data : []
  const total = arr.length
  const totalAmount = arr.reduce((sum, bp) => sum + bp.totalAmount, 0)
  const totalOutstanding = arr.reduce((sum, bp) => sum + bp.outstandingAmount, 0)
  const totalInCollector = arr.reduce((sum, bp) => sum + (bp.paymentStatus === PaymentStatus.IN_COLLECTOR ? bp.totalAmount : 0), 0)
  const totalCompleted = arr.reduce((sum, bp) => sum + (bp.paymentStatus === PaymentStatus.COMPLETED ? bp.totalAmount : 0), 0)
  const pendingCount = arr.filter(bp => bp.paymentStatus === PaymentStatus.PENDING).length
  const inCollectorCount = arr.filter(bp => bp.paymentStatus === PaymentStatus.IN_COLLECTOR).length
  const completedCount = arr.filter(bp => bp.paymentStatus === PaymentStatus.COMPLETED).length
  const pendingAmount = arr.reduce((sum, bp) => sum + (bp.paymentStatus === PaymentStatus.PENDING ? bp.totalAmount : 0), 0)
  const inCollectorAmount = arr.reduce((sum, bp) => sum + (bp.paymentStatus === PaymentStatus.IN_COLLECTOR ? bp.totalAmount : 0), 0)
  const completedAmount = arr.reduce((sum, bp) => sum + (bp.paymentStatus === PaymentStatus.COMPLETED ? bp.totalAmount : 0), 0)
  const selected = arr.filter(bp => selectedIds.has(bp.paymentId))
  const unselected = arr.filter(bp => !selectedIds.has(bp.paymentId))
  const selectedAmount = selected.reduce((sum, bp) => sum + bp.totalAmount, 0)
  const selectedOutstanding = selected.reduce((sum, bp) => sum + bp.outstandingAmount, 0)
  const unselectedAmount = unselected.reduce((sum, bp) => sum + bp.totalAmount, 0)
  const unselectedOutstanding = unselected.reduce((sum, bp) => sum + bp.outstandingAmount, 0)

  return {
    total,
    totalAmount,
    totalOutstanding,
    totalInCollector,
    totalCompleted,
    pendingCount,
    pendingAmount,
    inCollectorCount,
    inCollectorAmount,
    completedCount,
    completedAmount,
    selectedCount: selected.length,
    selectedAmount,
    selectedOutstanding,
    unselectedCount: unselected.length,
    unselectedAmount,
    unselectedOutstanding,
  }
}
